// src/sectors/pilates/staff/modal/staffLoginModal/CreateLoginModal.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import {
  PERMS_SCHEMA,
  defaultPerms,
  groupPerms,
  normalizePerms,
  applyRequires,
} from '../../permission/permsSchema';

// ✅ All => Own gibi link kuralları
function applyLinks(next) {
  const out = { ...(next || {}) };

  if (out.canViewMembersAll) out.canViewMembersOwn = true;
  if (out.canEditMembersAll) out.canEditMembersOwn = true;

  if (out.canViewSessionsAll) out.canViewSessionsOwn = true;
  if (out.canEditSessionsAll) out.canEditSessionsOwn = true;

  // ✅ Attendance All => Own
  if (out.canMarkAttendanceAll) out.canMarkAttendanceOwn = true;

  return out;
}

const empty = () => ({
  username: '',
  password: '',
  permissions: applyLinks(defaultPerms(PERMS_SCHEMA)),
});

export function CreateLoginModal({ open, staff, onClose, onSubmit }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const grouped = React.useMemo(() => groupPerms(PERMS_SCHEMA), [PERMS_SCHEMA]);
  const [form, setForm] = React.useState(empty());
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [permsOpen, setPermsOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setErr(null);
    setLoading(false);
    setForm(empty());
    setPermsOpen(false);
  }, [open]);

  const selectedPermCount = React.useMemo(() => {
    return Object.values(form.permissions || {}).filter(Boolean).length;
  }, [form.permissions]);

  const canSubmit =
    String(form.username || '').trim().length >= 3 && String(form.password || '').length >= 6;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const setPerm = (key, value) =>
    setForm((s) => {
      const nextPerms = applyRequires(s.permissions, key, value, PERMS_SCHEMA);
      return { ...s, permissions: applyLinks(nextPerms) };
    });

  const submit = async () => {
    setErr(null);

    const body = {
      username: String(form.username || '').trim(),
      password: String(form.password || ''),
      permissions: normalizePerms(form.permissions, PERMS_SCHEMA),
    };

    if (!body.username) return setErr('Kullanıcı adı boş olamaz.');
    if (body.username.length < 3) return setErr('Kullanıcı adı en az 3 karakter olmalı.');
    if (body.password.length < 6) return setErr('Şifre en az 6 karakter olmalı.');

    try {
      setLoading(true);
      await onSubmit?.(body);
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Panel açma başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={loading ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        },
      }}
    >
      {!open ? null : (
        <>
          <DialogTitle
            sx={{
              p: 0,
              background: 'linear-gradient(180deg, rgba(255,122,0,.16), rgba(255,122,0,.06))',
            }}
          >
            <MDBox sx={{ px: 2, py: 1.6 }}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDBox
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '16px',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(255,122,0,.14)',
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }}>vpn_key</Icon>
                  </MDBox>

                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                      Panel Aç
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                      {staff?.fullName
                        ? `${staff.fullName} için kullanıcı hesabı oluştur`
                        : 'Kullanıcı hesabı oluştur'}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={onClose}
                  disabled={loading}
                  sx={{ borderRadius: '14px' }}
                >
                  Kapat
                </MDButton>
              </MDBox>
            </MDBox>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ p: 2 }}>
            <MDBox sx={{ display: 'grid', gap: 1.25 }}>
              <MDInput
                label="Kullanıcı adı"
                value={form.username}
                onChange={(e) => set('username', e.target.value)}
                helperText="En az 3 karakter"
                disabled={loading}
              />

              <MDInput
                label="Şifre"
                type="password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                helperText="En az 6 karakter"
                disabled={loading}
              />

              <Divider />

              <MDBox
                onClick={() => setPermsOpen((v) => !v)}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: permsOpen ? 'rgba(255,122,0,.06)' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  transition: 'all .15s ease',
                  '&:hover': { bgcolor: 'rgba(255,122,0,.07)' },
                }}
              >
                <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon sx={{ fontSize: 18, opacity: 0.85 }}>
                    {permsOpen ? 'expand_less' : 'expand_more'}
                  </Icon>

                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                      İzinlerini düzenle
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                      Yetkileri aç/kapat ve detayları düzenle
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <Chip
                  size="small"
                  variant="outlined"
                  label={`${selectedPermCount} seçili`}
                  sx={{ borderRadius: '10px' }}
                />
              </MDBox>

              <Collapse in={permsOpen} timeout={220} unmountOnExit>
                <MDBox sx={{ mt: 1.25, display: 'grid', gap: 1 }}>
                  <MDTypography variant="button" fontWeight="bold">
                    Yetkiler
                  </MDTypography>

                  <MDBox sx={{ display: 'grid', gap: 1 }}>
                    {Object.entries(grouped).map(([groupName, rows]) => (
                      <MDBox
                        key={groupName}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <MDBox sx={{ px: 1.5, py: 1, bgcolor: 'rgba(0,0,0,.02)' }}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ opacity: 0.85 }}>
                            {groupName}
                          </MDTypography>
                        </MDBox>

                        <Divider />

                        <MDBox sx={{ px: 1.5, py: 1, display: 'grid', gap: 0.75 }}>
                          {rows.map((row) => (
                            <MDBox key={row.baseKey} sx={{ py: 0.25 }}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ display: 'block', mb: 0.35 }}
                              >
                                {row.label}
                              </MDTypography>

                              <MDBox sx={{ display: 'grid', gap: 0.25 }}>
                                {(row.scopes || []).map((s) => (
                                  <FormControlLabel
                                    key={s.key}
                                    control={
                                      <Switch
                                        disabled={loading}
                                        checked={Boolean(form.permissions?.[s.key])}
                                        onChange={(e) => setPerm(s.key, e.target.checked)}
                                      />
                                    }
                                    label={s.label}
                                  />
                                ))}
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              </Collapse>

              {err ? (
                <MDBox
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.error.main}`,
                    color: theme.palette.error.main,
                  }}
                >
                  {err}
                </MDBox>
              ) : null}
            </MDBox>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2 }}>
            <MDButton
              variant="outlined"
              color="dark"
              sx={{ borderRadius: '14px' }}
              onClick={onClose}
              disabled={loading}
            >
              İptal
            </MDButton>

            <MDButton
              variant="gradient"
              color="warning"
              sx={{ borderRadius: '14px' }}
              disabled={!canSubmit || loading}
              onClick={submit}
            >
              <Icon sx={{ mr: 0.5, fontSize: 18 }}>vpn_key</Icon>
              Panel Aç
            </MDButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
